import Spinner from "@/components/Spinner";
import { useHypertensionPatientsQuery, useOrganizationQuery } from "@/data/queries";
import { hasFeatureFlag } from "@/model/Organization";
import { HypertensionPatient } from "@/model/Patient";
import { formatDate, formatGestationalAge, isSameYearMonth } from "@/util/date-utils.ts";
import { useStickyValue } from "@/util/hooks/useStickyValue.ts";
import { LocalDate, ZoneId } from "@js-joda/core";
import orderBy from "lodash/orderBy";
import { useMemo, useState } from "react";
import HeaderCell from "./HeaderCell";
import { LiveInteractionDialog } from "./LiveInteractionDialog";
import { ClinicalStatusCell, ClinicalTimeCell, NoteButton } from "./PatientActionCells";
import PatientDataCell from "./PatientDataCell";
import { PatientEducationDialog } from "./PatientEducationDialog";
import PatientStatusCapsule from "./PatientStatusCapsule";
import PriorityIconWithTooltip from "./PriorityIconWithTooltip";
import { stickyDataCell, stickyHeaderCell } from "./stickyColumns";
import { StickyNoteDialog } from "./StickyNoteDialog";
import { daysSinceReviewFor, formatDays, patientStatusOrder } from "./Utils.tsx";
import ViewLink from "./ViewLink";

const isCritical = (patient: HypertensionPatient) => patient.unreviewedReadings.critical > 0;

const matchesSearch = (patient: HypertensionPatient, searchQuery: string) => {
  const query = searchQuery.trim().toLowerCase();
  if (query === "") {
    return true;
  }
  return [patient.firstName, patient.lastName, patient.medicalRecordNumber]
    .join(" ")
    .toLowerCase()
    .includes(query);
};

type ActiveDialog = { type: "live" | "education" | "note"; patient: HypertensionPatient };

type HypertensionColumns = {
  patientEducation: boolean;
  liveInteraction: boolean;
  clinicalTime: boolean;
  stickyNote: boolean;
};

const hypertensionGridTemplateColumns = (columns: HypertensionColumns) =>
  [
    "1fr", // Last name
    "1fr", // First name
    "1fr", // MRN
    "1fr", // GA
    "minmax(max-content, 1fr)", // Protocol
    columns.patientEducation && "auto",
    columns.liveInteraction && "auto",
    columns.clinicalTime && "auto",
    "1fr", // Journey status
    "1fr", // Days since review
    "3rem", // Priority icon
    columns.stickyNote && "3.5rem", // Note
    "6rem", // Action
  ]
    .filter(Boolean)
    .join(" ");

const priorityStickyRight = (columns: { stickyNote: boolean }) =>
  columns.stickyNote ? "right-38" : "right-24";

const HypertensionPopulationList = ({ searchQuery = "" }: { searchQuery?: string }) => {
  const { isFetching, isError, data: patients = [] } = useHypertensionPatientsQuery();
  const { data: organization } = useOrganizationQuery();

  const columns: HypertensionColumns = {
    patientEducation: hasFeatureFlag(organization, "PATIENT_EDUCATION_HYPERTENSION"),
    liveInteraction: hasFeatureFlag(organization, "TRACK_MONTHLY_LIVE_INTERACTION"),
    clinicalTime: hasFeatureFlag(organization, "TRACK_MONTHLY_CLINICAL_TIME"),
    stickyNote: hasFeatureFlag(organization, "CLINICIAN_APP_PATIENT_STICKY_NOTE"),
  };
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const displayActiveDialog = useStickyValue(activeDialog);

  const currentMonthLabel = formatDate("MMM yyyy", LocalDate.now());
  const gridTemplateColumns = hypertensionGridTemplateColumns(columns);

  const sortedPatients = useMemo(
    () =>
      orderBy(
        patients.filter((patient) => matchesSearch(patient, searchQuery)),
        [
          (patient) => patientStatusOrder(patient.status),
          (patient) => isCritical(patient),
          (patient) => daysSinceReviewFor(patient) ?? Number.MIN_VALUE,
          (patient) => patient.lastName,
          (patient) => patient.firstName,
        ],
        ["asc", "desc", "desc", "asc", "asc"]
      ),
    [patients, searchQuery]
  );

  return (
    <>
      <div className="overflow-x-auto">
        <div className="grid" style={{ gridTemplateColumns }}>
          <HeaderCell className="pe-6 ps-4">Last name</HeaderCell>
          <HeaderCell className="pe-6">First name</HeaderCell>
          <HeaderCell className="pe-6">MRN</HeaderCell>
          <HeaderCell className="pe-6">GA</HeaderCell>
          <HeaderCell className="pe-4">Protocol</HeaderCell>
          {columns.patientEducation && (
            <HeaderCell className="pe-4">
              <span title="Patient education">Patient ed.</span>
            </HeaderCell>
          )}
          {columns.liveInteraction && (
            <HeaderCell className="pe-4">
              <div className="flex flex-col">
                <span title="Live interaction">Live int.</span>
                <span className="text-xs font-normal text-primary/50">{currentMonthLabel}</span>
              </div>
            </HeaderCell>
          )}
          {columns.clinicalTime && (
            <HeaderCell className="pe-4">
              <div className="flex flex-col">
                <span title="Clinical time">Clin. time</span>
                <span className="text-xs font-normal text-primary/50">{currentMonthLabel}</span>
              </div>
            </HeaderCell>
          )}
          <HeaderCell className="pe-4">Journey status</HeaderCell>
          <HeaderCell className="pe-4">Days since review</HeaderCell>
          <HeaderCell
            className={stickyHeaderCell({
              rightClass: priorityStickyRight(columns),
              withGradient: true,
            })}
          />
          {columns.stickyNote && (
            <HeaderCell className={stickyHeaderCell({ rightClass: "right-24" })}>
              Note
            </HeaderCell>
          )}
          <HeaderCell className={stickyHeaderCell({ rightClass: "right-0" })}>Action</HeaderCell>
          {sortedPatients.map((patient, i) => (
            <HypertensionPatientRow
              key={`patient-${patient.id}`}
              patient={patient}
              rowIndex={i}
              columns={columns}
              onLogEducation={() => setActiveDialog({ type: "education", patient })}
              onLogLiveInteraction={() => setActiveDialog({ type: "live", patient })}
              onOpenNote={() => setActiveDialog({ type: "note", patient })}
            />
          ))}
        </div>
      </div>
      {isFetching && patients.length === 0 && (
        <div className="flex items-center justify-center gap-2 p-4">
          <Spinner />
          <span>Loading...</span>
        </div>
      )}
      {isError ? (
        <div className="p-4 text-center italic text-red-600">
          Failed to load patients. Please try again later.
        </div>
      ) : !isFetching && sortedPatients.length === 0 ? (
        <div className="p-4 text-center italic text-primary/60">
          {patients.length === 0
            ? "No active Bloomlife Connects hypertension orders. Come back soon!"
            : "No patients match your search."}
        </div>
      ) : null}

      {organization && displayActiveDialog && (
        <>
          <LiveInteractionDialog
            key={`live-interaction-dialog-${displayActiveDialog.patient.id}`}
            open={activeDialog?.type === "live"}
            onOpenChange={(open) => {
              if (!open) {
                setActiveDialog(null);
              }
            }}
            organization={organization}
            protocolType="hypertension"
            patientId={displayActiveDialog.patient.id}
            prescribingCaregiver={displayActiveDialog.patient.prescribingCaregiver}
          />
          <PatientEducationDialog
            key={`patient-education-dialog-${displayActiveDialog.patient.id}`}
            open={activeDialog?.type === "education"}
            onOpenChange={(open) => {
              if (!open) {
                setActiveDialog(null);
              }
            }}
            organization={organization}
            prescriptionId={displayActiveDialog.patient.prescriptionId}
          />
          <StickyNoteDialog
            key={`sticky-note-dialog-${displayActiveDialog.patient.id}`}
            open={activeDialog?.type === "note"}
            onOpenChange={(open) => {
              if (!open) {
                setActiveDialog(null);
              }
            }}
            note={displayActiveDialog.patient.stickyNote}
            prescriptionId={displayActiveDialog.patient.prescriptionId}
          />
        </>
      )}
    </>
  );
};

const HypertensionPatientRow = ({
  patient,
  rowIndex,
  columns,
  onLogEducation,
  onLogLiveInteraction,
  onOpenNote,
}: {
  patient: HypertensionPatient;
  rowIndex: number;
  columns: HypertensionColumns;
  onLogEducation: () => void;
  onLogLiveInteraction: () => void;
  onOpenNote: () => void;
}) => {
  const daysSinceReview = daysSinceReviewFor(patient);
  const isMonitoring = ["monitoring", "postpartum-monitoring"].includes(patient.status);
  const lastLiveInteractionDate =
    patient.lastLiveInteractionTime &&
    LocalDate.ofInstant(patient.lastLiveInteractionTime, ZoneId.SYSTEM);
  const educationProvidedAtDate = patient.educationProvidedAt
    ? LocalDate.ofInstant(patient.educationProvidedAt, ZoneId.SYSTEM)
    : null;
  const currentDate = LocalDate.now();
  const gestationalAge = patient.deliveryDate
    ? "Delivered"
    : patient.estimatedDateOfDelivery
      ? formatGestationalAge(patient.estimatedDateOfDelivery, LocalDate.now())
      : "-";

  return (
    <>
      <PatientDataCell
        rowIndex={rowIndex}
        title={patient.lastName}
        className="overflow-hidden pe-6 ps-4"
      >
        <span className="truncate">{patient.lastName}</span>
      </PatientDataCell>
      <PatientDataCell
        rowIndex={rowIndex}
        title={patient.firstName}
        className="overflow-hidden pe-6"
      >
        <span className="truncate">{patient.firstName}</span>
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-6">
        {patient.medicalRecordNumber}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-6">
        {gestationalAge}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="overflow-hidden pe-4">
        <span className="truncate" title={patient.personalizedClinicalProtocol.name}>
          {patient.personalizedClinicalProtocol.name ?? "-"}
        </span>
      </PatientDataCell>
      {columns.patientEducation && (
        <PatientDataCell rowIndex={rowIndex} className="pe-4">
          <ClinicalStatusCell
            date={educationProvidedAtDate ? formatDate("MMM d", educationProvidedAtDate) : null}
            onAdd={onLogEducation}
          />
        </PatientDataCell>
      )}
      {columns.liveInteraction && (
        <PatientDataCell rowIndex={rowIndex} className="pe-4">
          <ClinicalStatusCell
            date={
              lastLiveInteractionDate && isSameYearMonth(lastLiveInteractionDate, currentDate)
                ? formatDate("MMM d", lastLiveInteractionDate)
                : null
            }
            onAdd={onLogLiveInteraction}
          />
        </PatientDataCell>
      )}
      {columns.clinicalTime && (
        <PatientDataCell rowIndex={rowIndex} className="pe-4">
          <ClinicalTimeCell minutes={patient.clinicalTimeForCurrentMonth.toMinutes()} />
        </PatientDataCell>
      )}
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        <PatientStatusCapsule status={patient.status} />
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        {isMonitoring && daysSinceReview ? formatDays(Math.round(daysSinceReview)) : "-"}
      </PatientDataCell>
      <PatientDataCell
        rowIndex={rowIndex}
        className={stickyDataCell({
          rightClass: priorityStickyRight(columns),
          rowIndex,
          withGradient: true,
        })}
      >
        {isCritical(patient) && (
          <PriorityIconWithTooltip
            arrow="double"
            arrowDirection="up"
            color="text-measurement-text-emergency"
            tooltipText="Critical high blood pressure"
          />
        )}
      </PatientDataCell>
      {columns.stickyNote && (
        <PatientDataCell
          rowIndex={rowIndex}
          className={stickyDataCell({ rightClass: "right-24", rowIndex })}
        >
          <NoteButton unread={false} onClick={onOpenNote} />
        </PatientDataCell>
      )}
      <PatientDataCell
        rowIndex={rowIndex}
        className={stickyDataCell({ rightClass: "right-0", rowIndex })}
      >
        <ViewLink to={`/patients/${patient.id}/hypertension`} />
      </PatientDataCell>
    </>
  );
};

export default HypertensionPopulationList;
